import { useEffect } from 'react';

import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';
import { workQueueSnoozeCreate } from '~/data/generated/endpoints/work-queue/work-queue';
import type { ActionItemFilterParams } from '~/data/schemas/actionItem';

import { useActionItemsInfinite } from './useActionItems';

// Backlog list hook. Action items are fully backend-driven now: severity,
// descriptions, which event types exist, and snooze all live on the server.
// Snooze is a shared "temporarily dismiss until" on the item itself, so the
// backend already excludes snoozed items from the list — this hook just exposes
// snooze/refresh, which revalidate every action-items consumer (list + badge).
export function useInboxActionItems(
  params: ActionItemFilterParams = {},
  options: { refreshInterval?: number; loadAll?: boolean } = {},
) {
  const { loadAll = false, ...swrOptions } = options;
  const real = useActionItemsInfinite(params, swrOptions);
  const { token, refresh: signalRefresh } = useActionItemsRefresh();

  // The Work Queue's grid sorts, filters and pages client-side, and the summary
  // band above it totals the whole queue rather than the visible page — both
  // need every item in scope, not the first response. Pull the next page as
  // each one lands; `hasMore` goes false on the last. Guarded on `error` as
  // well as `isValidating`, or a failed page would be retried forever.
  useEffect(() => {
    if (!loadAll) return;
    if (real.hasMore && !real.isValidating && !real.error) void real.loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAll, real.hasMore, real.isValidating, real.error]);

  // Revalidate this hook's list whenever any consumer signals a refresh
  // (e.g. an invoice action resolved an item, or another tab snoozed one).
  useEffect(() => {
    if (token > 0) void real.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Signal every action-items consumer (this list + the sidebar badge) to
  // revalidate. Each responds by re-fetching its own SWR cache.
  const refresh = () => signalRefresh();

  // Snooze persists on the item (shared); after it succeeds the backend drops
  // it from the list, so revalidate every consumer to reflect that.
  const snooze = async (id: string, days: number) => {
    await workQueueSnoozeCreate(id, { days });
    signalRefresh();
  };

  return {
    actionItems: real.actionItems,
    count: real.count,
    hasMore: real.hasMore,
    loadMore: real.loadMore,
    isLoading: real.isLoading,
    isLoadingMore: real.isLoadingMore,
    isValidating: real.isValidating,
    error: real.error,
    snooze,
    refresh,
  };
}
